"use client";

import Linkify from "linkify-react";
import { DateTime } from "luxon";
import { notFound, useSearchParams } from "next/navigation";
import { EventImage } from "~/components/v3/events-components/EventImage";
import { EventImageBadgeOverlay } from "~/components/v3/events-components/EventImageOverlay";

import { useCallback, useEffect, useRef, useState } from "react";
import { RiLinkM } from "react-icons/ri";
import ShareEventModal from "~/app/events/[slug]/share/_components/ShareEventModal";
import { useEventContextV2, usePermissionsContext } from "~/app/providers";
import EventSponsor from "~/components/v3/events-components/EventSponsor";
import { cn } from "~/utils/fontsConfig";
import { DeleteExternalLinkButton } from "../manage/hosts/HostPageActions";
import { EventInviteActions } from "./EventActions";
import { EventChat } from "./EventChat";
import EventCreatorActions from "./EventCreatorActions";
import { EventDetails } from "./EventDetails";
import { EventNameHeader } from "./EventHeaders";
import { Infobox } from "./Infobox";
import { PostEvent } from "./PostEvent";
import { ShowEventImages } from "./ShowEventImages";
import { ShowEventNotes } from "./ShowEventNotes";
import { WhoIsGoing } from "./WhoIsGoing";
import { EventHosts } from "./EventHosts";

export const EventPage = ({
    isManagePage = false,
}: {
    isManagePage?: boolean;
}) => {
    const event = useEventContextV2();
    const permissions = usePermissionsContext();
    const searchParams = useSearchParams();

    if (!event) notFound();

    const action = searchParams?.get("action");

    const eventImagesRef = useRef<HTMLHeadingElement>(null);
    const [shareModalOpen, setShareModalOpen] = useState(false);

    const isAuthorized =
        permissions.isHost || permissions.isAdmin || permissions.isManager;
    const isAdminOrManager = permissions.isAdmin || permissions.isManager;

    useEffect(() => {
        if (eventImagesRef.current && action === "photo") {
            eventImagesRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [action]); // no dependencies (run once)

    const getEventStartTimeInUserTimezone = () => {
        const eventStartTime = DateTime.fromJSDate(event.startTime, {
            zone: "UTC",
        }).setZone(event.timezone, { keepLocalTime: true });
        const userZone = DateTime.local().zoneName;

        return eventStartTime.setZone(userZone);
    };

    const handleOnUpload = useCallback(() => {
        if (action !== "photo") return;
        setShareModalOpen(true);
    }, [action]);

    const eventHasEnded = getEventStartTimeInUserTimezone() < DateTime.now();

    return (
        <>
            {!isManagePage && (
                <span className="md:hidden">
                    <EventCreatorActions />
                </span>
            )}

            <div className="flex w-full items-center justify-center">
                <section className="grid w-full grid-cols-1 gap-2 md:grid-cols-[calc(60%-4px)_calc(40%-4px)]">
                    <div className="flex flex-col gap-5 rounded-md bg-v3-N-600 p-4 md:p-6">
                        {!isManagePage && (
                            <EventNameHeader
                                event={event}
                                variant="condensed"
                            />
                        )}

                        {event && (
                            <div className="relative aspect-[16/9] overflow-hidden rounded-[4px]">
                                <EventImage
                                    event={event}
                                    width={200}
                                    height={150}
                                    className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                                    alt={event.name}
                                    options={{
                                        type: "cover",
                                    }}
                                    isReadonly
                                />
                                <EventImageBadgeOverlay
                                    badges={event.eventBadges}
                                    className="bottom-[15px] md:bottom-4"
                                    size="large"
                                />
                            </div>
                        )}

                        <div className="flex flex-col-reverse gap-5">
                            <div className="flex flex-col gap-5 md:flex-col-reverse">
                                {isManagePage && (
                                    <EventInviteActions className="ml-auto flex items-start justify-end md:items-center" />
                                )}
                                <WhoIsGoing
                                    event={event}
                                    isManagePage={isManagePage}
                                />
                            </div>

                            <div className="flex flex-col gap-4 md:hidden">
                                <EventDetails
                                    event={event}
                                    isManagePage={isManagePage}
                                />
                                <EventHosts
                                    isAuthorized={!!isAuthorized}
                                    className="p-0"
                                />
                            </div>
                        </div>

                        {/* show at top if there are photos already uploaded */}
                        {!!event.userImages.length && eventHasEnded && (
                            <ShowEventImages
                                ref={eventImagesRef}
                                onSuccessfulUpload={handleOnUpload}
                            />
                        )}
                        <div className="whitespace-pre-wrap text-base text-v3-N-100">
                            <p className="break-words font-medium">
                                <Linkify
                                    options={{
                                        className:
                                            "text-v3-N300 hover:underline hover:text-v3-N100",
                                        target: "_blank",
                                        rel: "noopener noreferrer nofollow",
                                    }}
                                >
                                    {event.description}
                                </Linkify>
                            </p>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            {event.tags.map((tag) => (
                                <div
                                    key={tag}
                                    className="rounded-md bg-v3-N-400 p-2 text-sm"
                                >
                                    {tag}
                                </div>
                            ))}
                        </div>

                        {isAuthorized && !eventHasEnded && (
                            <ShowEventNotes
                                title="Host Notes"
                                info={event.showNotes ?? ""}
                            />
                        )}

                        {isAdminOrManager && !eventHasEnded && (
                            <ShowEventNotes
                                title="Community Manager Notes"
                                info={event.commManagerNotes ?? ""}
                            />
                        )}
                        {eventHasEnded && <PostEvent />}

                        {/* show at bottom if there are no photos uploaded yet and event has ended */}
                        {!event.userImages.length && eventHasEnded && (
                            <ShowEventImages
                                ref={eventImagesRef}
                                onSuccessfulUpload={handleOnUpload}
                            />
                        )}
                    </div>

                    <div className="flex flex-col gap-2">
                        <div
                            className={cn(
                                "flex-col gap-5 rounded-md bg-v3-N-600",
                                "hidden p-6 md:flex",
                            )}
                        >
                            {!isManagePage && <EventCreatorActions />}
                            <EventDetails
                                event={event}
                                isManagePage={isManagePage}
                            />
                        </div>

                        <EventHosts
                            isAuthorized={!!isAuthorized}
                            className="hidden md:flex"
                        />

                        {event.externalEventUrl && (
                            <div className="flex flex-col gap-5 rounded-md bg-v3-N-600 p-6">
                                <h2 className="text-base text-v3-N-300">
                                    Linked Event{" "}
                                    <span className="ml-2 max-h-fit rounded-lg bg-v3-primary px-3 py-1 text-sm text-black">
                                        External
                                    </span>
                                </h2>
                                <div className="flex items-center justify-between gap-2 rounded-[10px] bg-v3-N-500 px-4 py-3">
                                    <RiLinkM className="h-5 w-5" />
                                    <a
                                        className="mr-auto text-base underline"
                                        href={event.externalEventUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {event.externalEventUrl}
                                    </a>
                                    {isAuthorized && (
                                        <DeleteExternalLinkButton
                                            eventId={event.id}
                                        />
                                    )}
                                </div>
                            </div>
                        )}

                        {!isManagePage && (
                            <>
                                <EventSponsor
                                    event={event}
                                    permissions={permissions}
                                />
                                <EventChat
                                    event={event}
                                    permissions={permissions}
                                />
                            </>
                        )}
                    </div>

                    <div className="flex flex-col gap-5 rounded-md bg-v3-N-600 p-2 md:p-6">
                        <Infobox
                            community={event?.community}
                            profile={event?.user}
                            eventSlug={event.slug}
                            linkText={
                                event?.community
                                    ? "Go to community"
                                    : "Go to profile"
                            }
                        />
                    </div>
                </section>
            </div>
            {shareModalOpen && (
                <ShareEventModal
                    isVisible={shareModalOpen}
                    setIsVisible={setShareModalOpen}
                    event={event}
                    useUserUploadedImage={true}
                />
            )}
        </>
    );
};
